import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AuthService } from '../_services/auth.service';
import { ApiService } from '../_services/api.service';
import { User } from '../_interfaces/user';
import {
  FirstLoginStep,
  FirstLoginData,
  OptionalStepState,
} from './first-login.models';

export type FirstLoginState =
  | { type: 'idle' }
  | { type: 'loading' }
  | { type: 'error'; message: string }
  | { type: 'resendCooldown'; secondsRemaining: number };

@Injectable({ providedIn: 'root' })
export class FirstLoginService {
  private _currentStep$ = new BehaviorSubject<FirstLoginStep>(FirstLoginStep.License);
  private _state$ = new BehaviorSubject<FirstLoginState>({ type: 'idle' });
  private _data!: FirstLoginData;

  currentStep$ = this._currentStep$.asObservable();
  state$ = this._state$.asObservable();

  /** Session flag: once the user enters FTL, guards won't redirect back. Reset on logout. */
  hasNavigatedToFirstLogin = false;

  private cooldownTimer: any;

  constructor(
    private authService: AuthService,
    private apiService: ApiService,
  ) {}

  get currentStep(): FirstLoginStep {
    return this._currentStep$.value;
  }

  get data(): FirstLoginData {
    return this._data;
  }

  get state(): FirstLoginState {
    return this._state$.value;
  }

  /**
   * Determines whether a user needs to complete FTL.
   */
  static shouldShowFirstTimeLogin(user: User): boolean {
    const s = user.settings;
    if (!s.eula && !s.agreed_at) return true;
    if (s.isTempPass) return true;
    if (!s.phoneVerified && !s.skipPhone) return true;
    if (!s.activeEmail && !s.skipEmail) return true;
    const isLegal = user.entity?.settings?.category === 'Legal';
    if (!isLegal && !s.copilotSpeciality) return true;
    if (!Array.isArray(s.copilotTemplates)) return true;
    return false;
  }

  /**
   * Initialise the FTL flow from the current user state.
   */
  async initialize(): Promise<void> {
    const user = this.authService.getUser();
    if (!user) return;
    this.hasNavigatedToFirstLogin = true;
    this._data = this.buildDataFromUser(user);

    const isLegal = user.entity?.settings?.category === 'Legal';
    if (isLegal && !user.settings.copilotSpeciality) {
      try {
        await this.authService.updateUser({ settings: { copilotSpeciality: 'Legal' } });
        this._data.speciality.speciality = 'Legal';
        this._data.speciality.state = OptionalStepState.Completed;
      } catch (e) {
        console.error('Failed to auto-set Legal speciality:', e);
      }
    }

    this.configInitialStep();
  }

  resetState(): void {
    this._state$.next({ type: 'idle' });
  }

  // --- Step Navigation ---

  getDisplayableSteps(): FirstLoginStep[] {
    const steps: FirstLoginStep[] = [
      FirstLoginStep.License,
      FirstLoginStep.Password,
      FirstLoginStep.PhoneNumber,
    ];
    if (this._data.phone.verificationSentAt) {
      steps.push(FirstLoginStep.PhoneConfirmation);
    }
    steps.push(FirstLoginStep.ContactEmail);
    if (this._data.email.verificationSentAt) {
      steps.push(FirstLoginStep.ContactEmailConfirmation);
    }
    if (this._data.email.isVerified) {
      steps.push(FirstLoginStep.ContactEmailSuccess);
    }
    if (!this.isLegalOrg()) {
      steps.push(FirstLoginStep.Speciality);
    }
    steps.push(
      FirstLoginStep.Templates,
      FirstLoginStep.Done,
    );
    return steps;
  }

  /** Visible progress steps (excludes confirmation/success sub-steps and Done). */
  getProgressSteps(): FirstLoginStep[] {
    const steps = [
      FirstLoginStep.License,
      FirstLoginStep.Password,
      FirstLoginStep.PhoneNumber,
      FirstLoginStep.ContactEmail,
    ];
    if (!this.isLegalOrg()) {
      steps.push(FirstLoginStep.Speciality);
    }
    steps.push(FirstLoginStep.Templates);
    return steps;
  }

  getProgressIndex(): number {
    const steps = this.getProgressSteps();
    // Map sub-steps to their parent
    let mapped = this.currentStep;
    if (mapped === FirstLoginStep.PhoneConfirmation) mapped = FirstLoginStep.PhoneNumber;
    if (mapped === FirstLoginStep.ContactEmailConfirmation || mapped === FirstLoginStep.ContactEmailSuccess) {
      mapped = FirstLoginStep.ContactEmail;
    }
    if (mapped === FirstLoginStep.Done) return steps.length;
    const idx = steps.indexOf(mapped);
    return idx >= 0 ? idx : 0;
  }

  nextStep(): void {
    const displayable = this.getDisplayableSteps();
    const currentIdx = displayable.indexOf(this.currentStep);
    // Find the next incomplete step
    for (let i = currentIdx + 1; i < displayable.length; i++) {
      if (!this.isStepCompleted(displayable[i]) || displayable[i] === FirstLoginStep.Done) {
        this._currentStep$.next(displayable[i]);
        return;
      }
    }
    // All steps done
    this._currentStep$.next(FirstLoginStep.Done);
  }

  previousStep(): void {
    const displayable = this.getDisplayableSteps();
    const currentIdx = displayable.indexOf(this.currentStep);
    if (currentIdx > 0) {
      this._currentStep$.next(displayable[currentIdx - 1]);
    }
  }

  // --- Step actions ---

  async acceptLicense(): Promise<void> {
    this._state$.next({ type: 'loading' });
    try {
      await this.authService.updateUser({ settings: { eula: true } });
      this._data.licenseAgreed = true;
      this._state$.next({ type: 'idle' });
      this.nextStep();
    } catch (e: any) {
      this._state$.next({ type: 'error', message: e?.error?.message || e?.message || 'Failed to accept licence' });
    }
  }

  async savePassword(currentPassword: string, newPassword: string): Promise<void> {
    this._state$.next({ type: 'loading' });
    try {
      await this.authService.updateUser({
        password: { current_password: currentPassword, new_password: newPassword },
      });
      this._data.password.valid = true;
      this._state$.next({ type: 'idle' });
      this.nextStep();
    } catch (e: any) {
      const message = e?.error?.message || e?.message || 'Failed to change password';
      this._state$.next({ type: 'error', message });
    }
  }

  async sendPhoneVerification(phoneNumber: string, countryCode: string): Promise<void> {
    this._state$.next({ type: 'loading' });
    try {
      await this.authService.updateUser({
        phone: { number: phoneNumber, countryCode },
      });
      // If we get here or get a verify-sms 401, it's success
      this._data.phone.phoneNumber = phoneNumber;
      this._data.phone.countryCode = countryCode;
      this._data.phone.verificationSentAt = new Date();
      this._state$.next({ type: 'idle' });
      this._currentStep$.next(FirstLoginStep.PhoneConfirmation);
    } catch (e: any) {
      const message = e?.error?.message || e?.message || '';
      if (message === 'verify-sms' || message === 'verify-email') {
        // This is expected - verification code was sent
        this._data.phone.phoneNumber = phoneNumber;
        this._data.phone.countryCode = countryCode;
        this._data.phone.verificationSentAt = new Date();
        this._state$.next({ type: 'idle' });
        this._currentStep$.next(FirstLoginStep.PhoneConfirmation);
      } else {
        this._state$.next({ type: 'error', message: message || 'Failed to send phone verification' });
      }
    }
  }

  async verifyPhoneCode(confirmationCode: string): Promise<boolean> {
    this._state$.next({ type: 'loading' });
    try {
      await this.authService.updateUser({
        phone: {
          number: this._data.phone.phoneNumber,
          countryCode: this._data.phone.countryCode,
          confirmationCode,
        },
      });
      this._data.phone.state = OptionalStepState.Completed;
      this._state$.next({ type: 'idle' });
      this.nextStep();
      return true;
    } catch (e: any) {
      this._state$.next({ type: 'error', message: 'Incorrect code. Try again' });
      return false;
    }
  }

  async resendPhoneCode(): Promise<void> {
    this.startResendCooldown();
    try {
      await this.authService.updateUser({
        phone: {
          number: this._data.phone.phoneNumber,
          countryCode: this._data.phone.countryCode,
        },
      });
    } catch (e: any) {
      const message = e?.error?.message || e?.message || '';
      // verify-sms response is expected (code sent successfully)
      if (message !== 'verify-sms' && message !== 'verify-email') {
        this._state$.next({ type: 'error', message: message || 'Failed to resend code' });
      }
    }
  }

  remindPhoneLater(): void {
    this._data.phone.confirmationShown = true;
    this._data.phone.verificationSentAt = null;
    this.nextStep();
  }

  async skipPhone(): Promise<void> {
    this._state$.next({ type: 'loading' });
    try {
      await this.authService.updateUser({ phone: { skip: true } });
      this._data.phone.state = OptionalStepState.Skipped;
      this._data.phone.verificationSentAt = null;
      this._state$.next({ type: 'idle' });
      this.nextStep();
    } catch (e: any) {
      this._state$.next({ type: 'error', message: e?.error?.message || e?.message || 'Failed to skip phone' });
    }
  }

  async sendEmailVerification(email: string): Promise<void> {
    this._state$.next({ type: 'loading' });
    try {
      await this.authService.updateUser({ contactEmail: { email } });
      this._data.email.email = email;
      this._data.email.verificationSentAt = new Date();
      this._state$.next({ type: 'idle' });
      this._currentStep$.next(FirstLoginStep.ContactEmailConfirmation);
    } catch (e: any) {
      const message = e?.error?.message || e?.message || '';
      if (message === 'verify-email' || message === 'verify-sms') {
        this._data.email.email = email;
        this._data.email.verificationSentAt = new Date();
        this._state$.next({ type: 'idle' });
        this._currentStep$.next(FirstLoginStep.ContactEmailConfirmation);
      } else {
        this._state$.next({ type: 'error', message: message || 'Failed to send email verification' });
      }
    }
  }

  async verifyEmailCode(confirmationCode: string): Promise<boolean> {
    this._state$.next({ type: 'loading' });
    try {
      await this.authService.updateUser({
        contactEmail: {
          email: this._data.email.email,
          confirmationCode,
        },
      });
      this._data.email.state = OptionalStepState.Completed;
      this._data.email.isVerified = true;
      this._state$.next({ type: 'idle' });
      this._currentStep$.next(FirstLoginStep.ContactEmailSuccess);
      return true;
    } catch (e: any) {
      this._state$.next({ type: 'error', message: 'Incorrect code. Try again' });
      return false;
    }
  }

  async resendEmailCode(): Promise<void> {
    this.startResendCooldown();
    try {
      await this.authService.updateUser({
        contactEmail: { email: this._data.email.email },
      });
    } catch (e: any) {
      const message = e?.error?.message || e?.message || '';
      if (message !== 'verify-email' && message !== 'verify-sms') {
        this._state$.next({ type: 'error', message: message || 'Failed to resend code' });
      }
    }
  }

  remindEmailLater(): void {
    this._data.email.state = OptionalStepState.Skipped;
    this._data.email.confirmationShown = true;
    this._data.email.verificationSentAt = null;
    this.nextStep();
  }

  async skipEmail(): Promise<void> {
    this._state$.next({ type: 'loading' });
    try {
      await this.authService.updateUser({ contactEmail: { skip: true } });
      this._data.email.state = OptionalStepState.Skipped;
      this._data.email.verificationSentAt = null;
      this._state$.next({ type: 'idle' });
      this.nextStep();
    } catch (e: any) {
      this._state$.next({ type: 'error', message: e?.error?.message || e?.message || 'Failed to skip email' });
    }
  }

  markEmailConfirmationShown(): void {
    this._data.email.confirmationShown = true;
    this.nextStep();
  }

  async saveSpeciality(speciality: string): Promise<void> {
    this._state$.next({ type: 'loading' });
    try {
      await this.authService.updateUser({ settings: { copilotSpeciality: speciality } });
      this._data.speciality.speciality = speciality;
      this._data.speciality.state = OptionalStepState.Completed;
      this._state$.next({ type: 'idle' });
      this.nextStep();
    } catch (e: any) {
      this._state$.next({ type: 'error', message: e?.error?.message || e?.message || 'Failed to save speciality' });
    }
  }

  remindSpecialityLater(): void {
    this._data.speciality.state = OptionalStepState.Skipped;
    this.nextStep();
  }

  async saveTemplates(templates: { id: number; type: string }[]): Promise<void> {
    this._state$.next({ type: 'loading' });
    try {
      await this.authService.updateUser({ settings: { copilotTemplates: templates } });
      this._data.templates.selectedTemplates = templates;
      this._data.templates.state = OptionalStepState.Completed;
      this._state$.next({ type: 'idle' });
      this.nextStep();
    } catch (e: any) {
      this._state$.next({ type: 'error', message: e?.error?.message || e?.message || 'Failed to save blueprints' });
    }
  }

  // --- Helpers ---

  allowBackButton(): boolean {
    return (
      this.currentStep === FirstLoginStep.License ||
      this.currentStep === FirstLoginStep.Password ||
      this.currentStep === FirstLoginStep.PhoneConfirmation ||
      this.currentStep === FirstLoginStep.ContactEmailConfirmation
    );
  }

  canGoBack(): boolean {
    if (
      this.currentStep !== FirstLoginStep.PhoneConfirmation &&
      this.currentStep !== FirstLoginStep.ContactEmailConfirmation
    ) {
      return false;
    }
    const displayable = this.getDisplayableSteps();
    const currentIdx = displayable.indexOf(this.currentStep);
    if (currentIdx <= 0) return false;
    return !this.isStepCompleted(displayable[currentIdx - 1]);
  }

  private isStepCompleted(step: FirstLoginStep): boolean {
    switch (step) {
      case FirstLoginStep.License:
        return this._data.licenseAgreed;
      case FirstLoginStep.Password:
        return this._data.password.valid;
      case FirstLoginStep.PhoneNumber:
        return this._data.phone.state !== OptionalStepState.NotStarted;
      case FirstLoginStep.PhoneConfirmation:
        return this._data.phone.state === OptionalStepState.Completed;
      case FirstLoginStep.ContactEmail:
        return this._data.email.state !== OptionalStepState.NotStarted;
      case FirstLoginStep.ContactEmailConfirmation:
        return this._data.email.state === OptionalStepState.Completed;
      case FirstLoginStep.ContactEmailSuccess:
        return this._data.email.confirmationShown;
      case FirstLoginStep.Speciality:
        return this._data.speciality.state !== OptionalStepState.NotStarted;
      case FirstLoginStep.Templates:
        return this._data.templates.state !== OptionalStepState.NotStarted;
      case FirstLoginStep.Done:
        return false;
      default:
        return false;
    }
  }

  private buildDataFromUser(user: User): FirstLoginData {
    const s = user.settings;
    return {
      licenseAgreed: !!(s.eula || s.agreed_at),
      password: {
        valid: !s.isTempPass,
        currentPassword: '',
        newPassword: '',
        verifyPassword: '',
      },
      phone: {
        state: s.phoneVerified
          ? OptionalStepState.Completed
          : s.skipPhone
            ? OptionalStepState.Skipped
            : OptionalStepState.NotStarted,
        phoneNumber: user.profile?.phone || '',
        countryCode: s.countryCode || '+353',
        verificationSentAt: null,
        confirmationShown: false,
      },
      email: {
        state: s.activeEmail
          ? OptionalStepState.Completed
          : s.skipEmail
            ? OptionalStepState.Skipped
            : OptionalStepState.NotStarted,
        email: user.email || '',
        verificationSentAt: null,
        confirmationShown: false,
        isVerified: !!s.activeEmail,
      },
      speciality: {
        state: (s.copilotSpeciality || user.entity?.settings?.category === 'Legal')
          ? OptionalStepState.Completed
          : OptionalStepState.NotStarted,
        speciality: s.copilotSpeciality || (user.entity?.settings?.category === 'Legal' ? 'Legal' : ''),
      },
      templates: {
        state: Array.isArray(s.copilotTemplates)
          ? OptionalStepState.Completed
          : OptionalStepState.NotStarted,
        selectedTemplates: s.copilotTemplates || [],
      },
    };
  }

  private configInitialStep(): void {
    const user = this.authService.getUser();
    if (!user) return;
    const s = user.settings;

    if (!s.eula && !s.agreed_at) {
      this._currentStep$.next(FirstLoginStep.License);
      return;
    }
    if (s.isTempPass) {
      this._currentStep$.next(FirstLoginStep.Password);
      return;
    }
    if (!s.phoneVerified && !s.skipPhone) {
      this._currentStep$.next(FirstLoginStep.PhoneNumber);
      return;
    }
    if (!s.activeEmail && !s.skipEmail) {
      this._currentStep$.next(FirstLoginStep.ContactEmail);
      return;
    }
    const isLegal = user.entity?.settings?.category === 'Legal';
    if (!s.copilotSpeciality && !isLegal) {
      this._currentStep$.next(FirstLoginStep.Speciality);
      return;
    }
    if (!Array.isArray(s.copilotTemplates)) {
      this._currentStep$.next(FirstLoginStep.Templates);
      return;
    }
    this._currentStep$.next(FirstLoginStep.Done);
  }

  private isLegalOrg(): boolean {
    const user = this.authService.getUser();
    return user?.entity?.settings?.category === 'Legal';
  }

  private startResendCooldown(): void {
    let seconds = 5;
    this._state$.next({ type: 'resendCooldown', secondsRemaining: seconds });
    if (this.cooldownTimer) clearInterval(this.cooldownTimer);
    this.cooldownTimer = setInterval(() => {
      seconds--;
      if (seconds <= 0) {
        clearInterval(this.cooldownTimer);
        this.cooldownTimer = null;
        this._state$.next({ type: 'idle' });
      } else {
        this._state$.next({ type: 'resendCooldown', secondsRemaining: seconds });
      }
    }, 1000);
  }
}
